// DataGrid v2 (spec/dashboard.md section 2.3): virtualised, server-paged, column menu (sort / pin / hide with remembered
// prefs), selection with a bulk menu, inline cell editors, keyboard navigation, copy / paste, an empty state; a grid of
// thumbnails (`previews`) adds the Previews: Small / Large view switch, remembered with the prefs. The search box applies
// as you type after a 300 ms pause, Enter at once (section 6.1, ui/useDebounced.ts); `openOnClick` opens a row of a list
// on a single click (section 2.3), the editable grids keep double-click. A list longer than one page shows a pager under
// the grid (`pageSize`, 100 rows by default, the user's choice remembered with the prefs); a server-paged list loads
// forward until the page on screen is full.
import { observeElementRect, useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode, type Ref } from 'react';
import { Button, IconButton } from './Button';
import { ArrowDown, ArrowUp, CheckSquare, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Columns3, MoreVertical, Square } from './icons';
import { EmptyState } from './Banner';
import { Menu } from './Popover';
import { Select, TextInput } from './inputs';
import { useSearchBox } from './useDebounced';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: number | string;
  sortable?: boolean;
  /** the value the client sorts by when the API does not sort this column */
  sortValue?: (row: T) => string | number | null | undefined;
  /** the cell's text for copy */
  cellText?: (row: T) => string;
  /** an inline editor: render it with commit and cancel callbacks */
  editor?: (row: T, done: { commit: (value: unknown) => void; cancel: () => void }) => ReactNode;
  /** receives pasted text (Ctrl+V on the focused cell) */
  onPaste?: (row: T, text: string) => void;
  /** never hidden or moved (the id column) */
  fixed?: boolean;
  align?: 'left' | 'right';
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

export interface BulkAction {
  key: string;
  label: string;
  onSelect: (keys: string[]) => void;
  danger?: boolean;
  disabled?: boolean;
}

export interface DataGridProps<T> {
  /** the grid's identity for remembered column prefs (`hue.grid:<id>`) */
  id: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  caption?: ReactNode;
  emptyState?: ReactNode;
  loading?: boolean;
  onRowOpen?: (row: T) => void;
  /**
   * A list of things to open (organizations, games, branches): a single click on a row, outside its controls, calls
   * `onRowOpen` and the row shows a pointer. Absent: single click selects and double-click opens (the editable grids).
   */
  openOnClick?: boolean;
  rowClass?: (row: T) => string;
  selectable?: boolean;
  bulkActions?: BulkAction[];
  /** server paging: there is more; load it */
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** a server-paged list's count of every row when the API says it: the pager counts its pages by it */
  total?: number | null;
  /**
   * Rows per page when the user has not picked a size (`PAGE_SIZES`): a longer list shows a pager under the grid. `false`
   * keeps one scrolling list (a server-paged one then loads more as its last rows scroll into view).
   */
  pageSize?: number | false;
  /** the owner's filters: a change goes back to page 1 (the grid's own search box and sort do so by themselves) */
  pageResetKey?: string;
  /** a row to bring into view: when this becomes a row's key the grid turns to that row's page and focuses it (+ Create) */
  revealKey?: string | null;
  /** when given the API sorts; else the grid sorts the loaded rows by `sortValue` and says so */
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  search?: { value: string; onSearch: (text: string) => void; placeholder?: string };
  toolbar?: ReactNode;
  rowHeight?: number;
  height?: number | string;
  /** a change counter: the grid re-renders every visible row when it moves (after a Save All) */
  revision?: number;
  /**
   * The grid shows thumbnails (`.thumb` cells): the toolbar gets the Previews: Small / Large switch; Large sets
   * `data-previews="l"` on the grid (the stylesheet grows the thumbnails to `PREVIEW_THUMB` px) in `PREVIEW_ROW_H` rows.
   */
  previews?: boolean;
}

interface Prefs {
  hidden: string[];
  pinLeft: string[];
  pinRight: string[];
  /** column widths the user dragged (px) */
  widths: Record<string, number>;
  /** the view of a `previews` grid: large thumbnails (absent: the small ones in standard rows) */
  previews?: 'l';
  /** the rows per page the user picked in the pager */
  pageSize?: number;
}

/** The pager's choices of rows per page. */
export const PAGE_SIZES = [50, 100, 250, 500];
const DEFAULT_PAGE_SIZE = 100;
const formatCount = (n: number) => n.toLocaleString('en-US');

function loadPrefs(id: string): Prefs {
  try {
    const raw = localStorage.getItem(`hue.grid:${id}`);
    if (raw) return { hidden: [], pinLeft: [], pinRight: [], widths: {}, ...(JSON.parse(raw) as Partial<Prefs>) };
  } catch {
    // ignore
  }
  return { hidden: [], pinLeft: [], pinRight: [], widths: {} };
}

function savePrefs(id: string, prefs: Prefs): void {
  try {
    localStorage.setItem(`hue.grid:${id}`, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

const SELECT_WIDTH = 32;
/** the thumbnail of a Large previews row (`.grid-wrap[data-previews='l'] .thumb` in styles.css) and the row that holds it with 8 px above and below */
export const PREVIEW_THUMB = 80;
export const PREVIEW_ROW_H = PREVIEW_THUMB + 16;

/** The click landed on a control inside the row (a link, a button, an input …): it handles itself, the row does not open. */
function isInteractiveTarget(target: EventTarget, row: Element): boolean {
  let el = target instanceof Element ? target : null;
  while (el && el !== row) {
    if (el.matches('a[href], button, input, select, textarea, label, [role="button"], [role="menuitem"], [contenteditable="true"]')) return true;
    el = el.parentElement;
  }
  return false;
}

export function DataGrid<T>(props: DataGridProps<T>) {
  const { id, columns, rows, rowKey, caption, emptyState, loading, onRowOpen, openOnClick, rowClass, selectable, bulkActions = [], hasMore, onLoadMore, total, pageSize = DEFAULT_PAGE_SIZE, pageResetKey, revealKey, sort, onSortChange, search, toolbar, rowHeight = 34, height, revision, previews } = props;
  const [prefs, setPrefs] = useState<Prefs>(() => loadPrefs(id));
  const paged = pageSize !== false;
  const size = prefs.pageSize ?? (pageSize === false ? DEFAULT_PAGE_SIZE : pageSize);
  const large = Boolean(previews) && prefs.previews === 'l';
  const effectiveRowHeight = large ? PREVIEW_ROW_H : rowHeight;
  const setLarge = (on: boolean) =>
    setPrefs((p) => {
      const next = { ...p };
      if (on) next.previews = 'l';
      else delete next.previews;
      return next;
    });
  const [clientSort, setClientSort] = useState<SortState | null>(null);
  const effectiveSort = onSortChange ? (sort ?? null) : clientSort;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focused, setFocused] = useState<number>(-1);
  const [editing, setEditing] = useState<{ row: string; col: string } | null>(null);
  const [menu, setMenu] = useState<{ col: string; anchor: HTMLElement } | null>(null);
  const [columnsMenu, setColumnsMenu] = useState<HTMLElement | null>(null);
  const [bulkMenu, setBulkMenu] = useState<HTMLElement | null>(null);
  const searchBox = useSearchBox(search?.value ?? '', (text) => search?.onSearch(text));
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastClickIndex = useRef<number | null>(null);

  useEffect(() => savePrefs(id, prefs), [id, prefs]);

  const visible = useMemo(() => {
    const left = columns.filter((c) => prefs.pinLeft.includes(c.key) && !prefs.hidden.includes(c.key));
    const right = columns.filter((c) => prefs.pinRight.includes(c.key) && !prefs.hidden.includes(c.key));
    const middle = columns.filter((c) => !prefs.hidden.includes(c.key) && !prefs.pinLeft.includes(c.key) && !prefs.pinRight.includes(c.key));
    return [...left, ...middle, ...right];
  }, [columns, prefs]);

  const sortedRows = useMemo(() => {
    if (onSortChange || !clientSort) return rows;
    const col = columns.find((c) => c.key === clientSort.key);
    if (!col?.sortValue) return rows;
    const sv = col.sortValue;
    return [...rows].sort((a, b) => {
      const va = sv(a);
      const vb = sv(b);
      if (va === vb) return 0;
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      const r = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true });
      return clientSort.dir === 'asc' ? r : -r;
    });
  }, [rows, clientSort, columns, onSortChange]);

  const allKeys = useMemo(() => sortedRows.map(rowKey), [sortedRows, rowKey]);
  useEffect(() => {
    // rows that left the grid leave the selection
    const present = new Set(allKeys);
    setSelected((s) => {
      const next = new Set(Array.from(s).filter((k) => present.has(k)));
      return next.size === s.size ? s : next;
    });
  }, [allKeys]);

  // the pager: every row is counted once the list is loaded, else the server's total (or what is loaded so far)
  const totalKnown = !hasMore || (total !== null && total !== undefined);
  const count = hasMore ? Math.max(total ?? 0, sortedRows.length) : sortedRows.length;
  const pages = Math.max(1, Math.ceil(count / size));
  const [pageState, setPage] = useState(0);
  // a list of unknown length may turn one page past the rows it has: that page loads as it opens
  const page = paged ? Math.min(pageState, totalKnown ? pages - 1 : pages) : 0;
  const start = page * size;
  const pageRows = useMemo(() => (paged ? sortedRows.slice(start, start + size) : sortedRows), [paged, sortedRows, start, size]);
  const keys = useMemo(() => pageRows.map(rowKey), [pageRows, rowKey]);
  const showPager = paged && (count > size || page > 0 || (Boolean(hasMore) && count > 0));

  // a new search, sort or owner filter starts again at page 1
  const resetKey = [search?.value ?? '', effectiveSort ? `${effectiveSort.key}:${effectiveSort.dir}` : '', pageResetKey ?? ''].join('\u0000');
  const [resetSeen, setResetSeen] = useState(resetKey);
  if (resetSeen !== resetKey) {
    setResetSeen(resetKey);
    setPage(0);
  }

  const virtualizer = useVirtualizer({
    count: pageRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => effectiveRowHeight,
    overscan: 8,
    // an unmeasured scroll element (jsdom, a hidden tab) counts as a viewport of this size, so the first rows render
    initialRect: { width: 1200, height: 800 },
    observeElementRect: (instance, cb) => observeElementRect(instance, (rect) => cb({ width: rect.width || 1200, height: rect.height || 800 })),
  });
  useEffect(() => {
    // the row height moved (the Previews switch): the rows are laid out again from the new estimate
    virtualizer.measure();
  }, [effectiveRowHeight, virtualizer]);
  const items = virtualizer.getVirtualItems();
  const paddingTop = items.length > 0 ? items[0]!.start : 0;
  const paddingBottom = items.length > 0 ? virtualizer.getTotalSize() - items[items.length - 1]!.end : 0;

  useEffect(() => {
    if (!hasMore || !onLoadMore || loading) return;
    // a paged list loads forward until the page on screen is full; one scrolling list as its last rows come into view
    if (paged) {
      if (sortedRows.length < start + size) onLoadMore();
      return;
    }
    const last = items[items.length - 1];
    if (last && last.index >= sortedRows.length - 10) onLoadMore();
  }, [items, hasMore, onLoadMore, sortedRows.length, loading, paged, start, size]);

  useEffect(() => {
    // another page starts at its top
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [page]);

  // without a fixed `height` the grid takes the room left in the pane it scrolls in, so the pager under it stays on screen
  const pagerRef = useRef<HTMLElement>(null);
  const [fitHeight, setFitHeight] = useState<number | null>(null);
  const emptyList = sortedRows.length === 0;
  useLayoutEffect(() => {
    if (height !== undefined) return;
    const fit = () => {
      const el = scrollRef.current;
      if (!el) return;
      let pane = el.parentElement;
      while (pane && !/(auto|scroll)/.test(getComputedStyle(pane).overflowY)) pane = pane.parentElement;
      const viewHeight = pane ? pane.clientHeight : window.innerHeight;
      if (!viewHeight) return; // no layout (a test, a hidden tab): the stylesheet's height stays
      const top = el.getBoundingClientRect().top - (pane ? pane.getBoundingClientRect().top - pane.scrollTop : 0);
      const padding = pane ? Number.parseFloat(getComputedStyle(pane).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(viewHeight - top - (pagerRef.current?.offsetHeight ?? 0) - padding - 2));
      setFitHeight((h) => (h === next ? h : next));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [height, showPager, loading, emptyList]);

  const goTo = (p: number) => {
    setPage(Math.max(0, p));
    setFocused(-1);
    setEditing(null);
  };
  const setPageSize = (n: number) => {
    setPrefs((p) => ({ ...p, pageSize: n }));
    goTo(Math.floor(start / n)); // the first row on screen stays on screen
  };

  const revealed = useRef<string | null>(null);
  useEffect(() => {
    if (!revealKey || revealed.current === revealKey) return;
    const at = allKeys.indexOf(revealKey);
    if (at < 0) return; // not among the rows (yet, or filtered out)
    revealed.current = revealKey;
    if (paged) setPage(Math.floor(at / size));
    setFocused(paged ? at % size : at);
  }, [revealKey, allKeys, paged, size]);

  const toggleSort = (key: string) => {
    const next: SortState | null = effectiveSort?.key !== key ? { key, dir: 'asc' } : effectiveSort.dir === 'asc' ? { key, dir: 'desc' } : null;
    if (onSortChange) onSortChange(next);
    else setClientSort(next);
  };

  const setPin = (key: string, side: 'left' | 'right' | null) =>
    setPrefs((p) => ({
      ...p,
      pinLeft: side === 'left' ? Array.from(new Set([...p.pinLeft, key])) : p.pinLeft.filter((k) => k !== key),
      pinRight: side === 'right' ? Array.from(new Set([...p.pinRight, key])) : p.pinRight.filter((k) => k !== key),
    }));
  const setHidden = (key: string, hidden: boolean) => setPrefs((p) => ({ ...p, hidden: hidden ? Array.from(new Set([...p.hidden, key])) : p.hidden.filter((k) => k !== key) }));

  const toggleRow = (index: number, shift: boolean) => {
    const key = keys[index];
    if (key === undefined) return;
    const anchor = lastClickIndex.current; // read now: the updater runs after the anchor moved to this row
    setSelected((s) => {
      const next = new Set(s);
      if (shift && anchor !== null) {
        const [from, to] = [Math.min(anchor, index), Math.max(anchor, index)];
        for (let i = from; i <= to; i++) next.add(keys[i]!);
      } else if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    lastClickIndex.current = index;
  };

  const startResize = (e: ReactMouseEvent<HTMLElement>, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.parentElement;
    const startX = e.clientX;
    const startW = th?.getBoundingClientRect().width || prefs.widths[key] || 120;
    const move = (ev: globalThis.MouseEvent) => setPrefs((p) => ({ ...p, widths: { ...p.widths, [key]: Math.max(40, Math.round(startW + ev.clientX - startX)) } }));
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };
  const resetWidth = (key: string) =>
    setPrefs((p) => {
      const widths = { ...p.widths };
      delete widths[key];
      return { ...p, widths };
    });

  /** The cell Tab moves the inline editor to: the next editable column, then the first editable column of the next row. */
  const nextEditable = (rowIndex: number, colKey: string, backwards: boolean): { row: string; col: string } | null => {
    const editable = visible.filter((c) => c.editor);
    const at = editable.findIndex((c) => c.key === colKey);
    let r = rowIndex;
    let ci = at + (backwards ? -1 : 1);
    if (ci < 0) {
      r -= 1;
      ci = editable.length - 1;
    }
    if (ci >= editable.length) {
      r += 1;
      ci = 0;
    }
    const row = pageRows[r];
    const col = editable[ci];
    return row && col ? { row: rowKey(row), col: col.key } : null;
  };

  // the header checkbox is the page's: it selects the rows on screen and keeps the other pages' selection
  const allSelected = keys.length > 0 && keys.every((k) => selected.has(k));
  const someSelected = !allSelected && keys.some((k) => selected.has(k));
  const togglePage = () =>
    setSelected((s) => {
      const next = new Set(s);
      for (const k of keys) {
        if (allSelected) next.delete(k);
        else next.add(k);
      }
      return next;
    });
  const headerCheck = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerCheck.current) headerCheck.current.indeterminate = someSelected;
  }, [someSelected]);

  const runBulk = (action: BulkAction) => {
    action.onSelect(Array.from(selected));
    setSelected(new Set()); // the operation consumed the selection (Q-D-20)
  };

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (editing) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;
      const meta = e.ctrlKey || e.metaKey;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocused((f) => Math.min(pageRows.length - 1, f + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocused((f) => Math.max(0, f - 1));
      } else if (e.key === 'Enter' && focused >= 0) {
        e.preventDefault();
        const row = pageRows[focused];
        if (row) onRowOpen?.(row);
      } else if (e.key === ' ' && selectable && focused >= 0) {
        e.preventDefault();
        toggleRow(focused, e.shiftKey);
      } else if (e.key === 'F2' && focused >= 0) {
        const row = pageRows[focused];
        const col = visible.find((c) => c.editor);
        if (row && col) setEditing({ row: rowKey(row), col: col.key });
      } else if (meta && e.key.toLowerCase() === 'a' && selectable) {
        e.preventDefault();
        setSelected(new Set(keys));
      } else if (meta && e.key.toLowerCase() === 'c' && focused >= 0) {
        const row = pageRows[focused];
        if (!row) return;
        const cell = target.closest<HTMLElement>('td[data-col]');
        const col = cell?.dataset.col ? visible.find((c) => c.key === cell.dataset.col) : undefined;
        const text = selected.size > 1
          ? Array.from(selected).map((k) => sortedRows[allKeys.indexOf(k)]).filter((r): r is T => r !== undefined).map((r) => visible.map((c) => c.cellText?.(r) ?? '').join('\t')).join('\n')
          : col
            ? (col.cellText?.(row) ?? cell?.textContent ?? '')
            : visible.map((c) => c.cellText?.(row) ?? '').join('\t');
        void navigator.clipboard?.writeText(text);
      }
    },
    [editing, focused, pageRows, sortedRows, allKeys, onRowOpen, selectable, keys, visible, selected, rowKey],
  );

  useEffect(() => {
    if (focused >= 0) virtualizer.scrollToIndex(focused);
  }, [focused, virtualizer]);

  const menuColumn = menu ? columns.find((c) => c.key === menu.col) : null;
  const sortLabel = effectiveSort && !onSortChange ? ' · sorted client-side' : '';

  return (
    <div className="grid-wrap" data-revision={revision} data-previews={large ? 'l' : undefined}>
      <div className="grid-toolbar">
        {caption !== undefined && <span className="grid-caption">{caption}{sortLabel}</span>}
        {toolbar}
        <span className="spacer" />
        {selectable && selected.size > 0 && (
          <>
            <Button size="s" onClick={(e) => setBulkMenu(e.currentTarget)} aria-haspopup="menu">
              Selected: {selected.size} <ChevronDown aria-hidden="true" />
            </Button>
            <Menu
              anchor={bulkMenu}
              open={bulkMenu !== null}
              onClose={() => setBulkMenu(null)}
              label="Bulk actions"
              items={[...bulkActions.map((a) => ({ key: a.key, label: a.label, onSelect: () => runBulk(a), danger: a.danger, disabled: a.disabled })), { key: 'sep', label: '', separator: true }, { key: 'clear', label: 'Clear selection', onSelect: () => setSelected(new Set()) }]}
            />
          </>
        )}
        {search && (
          <form
            className="row"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              searchBox.applyNow();
            }}
          >
            <TextInput size="s" className="input-inline" aria-label="Search" placeholder={search.placeholder ?? 'Search…'} value={searchBox.text} onChange={(e) => searchBox.setText(e.target.value)} />
          </form>
        )}
        {previews && (
          <span className="grid-view">
            <span className="grid-view-label" aria-hidden="true">
              Previews
            </span>
            <span className="segmented" role="radiogroup" aria-label="Preview size">
              {(['s', 'l'] as const).map((size) => {
                const on = large === (size === 'l');
                return (
                  <button key={size} type="button" role="radio" aria-checked={on} className={`tab ${on ? 'active' : ''}`} title={size === 'l' ? `Large previews: ${PREVIEW_THUMB} px thumbnails in tall rows` : 'Small previews: the standard rows'} onClick={() => setLarge(size === 'l')}>
                    {size === 'l' ? 'Large' : 'Small'}
                  </button>
                );
              })}
            </span>
          </span>
        )}
        <Button size="s" variant="ghost" icon={<Columns3 aria-hidden="true" />} onClick={(e) => setColumnsMenu(e.currentTarget)} aria-haspopup="menu">
          Columns
        </Button>
        <Menu
          anchor={columnsMenu}
          open={columnsMenu !== null}
          onClose={() => setColumnsMenu(null)}
          label="Columns"
          items={columns.filter((c) => !c.fixed).map((c) => ({ key: c.key, icon: prefs.hidden.includes(c.key) ? <Square aria-hidden="true" /> : <CheckSquare aria-hidden="true" />, label: typeof c.header === 'string' ? c.header : c.key, onSelect: () => setHidden(c.key, !prefs.hidden.includes(c.key)) }))}
        />
      </div>
      <div ref={scrollRef} className="grid-scroll" style={{ maxHeight: height ?? fitHeight ?? 'calc(100vh - 240px)' }} tabIndex={0} onKeyDown={onKeyDown} role="presentation">
        <table className="grid" role="grid" aria-rowcount={count} aria-busy={loading || undefined}>
          <thead>
            <tr role="row">
              {selectable && (
                <th scope="col" className="grid-cell-check pin-left" style={{ width: SELECT_WIDTH }}>
                  <input ref={headerCheck} type="checkbox" aria-label={showPager ? 'Select all on this page' : 'Select all'} checked={allSelected} onChange={togglePage} />
                </th>
              )}
              {visible.map((c) => {
                const pinnedL = prefs.pinLeft.includes(c.key);
                const pinnedR = prefs.pinRight.includes(c.key);
                const sorted = effectiveSort?.key === c.key ? effectiveSort.dir : undefined;
                return (
                  <th key={c.key} scope="col" className={pinnedL ? 'pin-left' : pinnedR ? 'pin-right' : ''} style={{ width: prefs.widths[c.key] ?? c.width, left: pinnedL && selectable ? SELECT_WIDTH : undefined, textAlign: c.align }} aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}>
                    <span className="th-inner">
                      <span>{c.header}</span>
                      {sorted && <span className="th-sort" aria-hidden="true">{sorted === 'asc' ? <ArrowUp /> : <ArrowDown />}</span>}
                      <button type="button" className="icon-button th-menu" aria-label={`Column menu: ${typeof c.header === 'string' ? c.header : c.key}`} onClick={(e) => setMenu({ col: c.key, anchor: e.currentTarget })}>
                        <MoreVertical aria-hidden="true" />
                      </button>
                    </span>
                    <span className="th-resize" role="separator" aria-orientation="vertical" aria-label={`Resize column ${typeof c.header === 'string' ? c.header : c.key}`} title="Drag to resize; double-click to reset" onMouseDown={(e) => startResize(e, c.key)} onDoubleClick={() => resetWidth(c.key)} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr aria-hidden="true">
                <td style={{ height: paddingTop, padding: 0, border: 'none' }} colSpan={visible.length + (selectable ? 1 : 0)} />
              </tr>
            )}
            {items.map((item) => {
              const row = pageRows[item.index]!;
              const key = rowKey(row);
              const isSelected = selected.has(key);
              return (
                <tr
                  key={key}
                  role="row"
                  aria-selected={selectable ? isSelected : undefined}
                  aria-rowindex={start + item.index + 1}
                  className={`grid-row ${isSelected ? 'selected' : ''} ${focused === item.index ? 'focused' : ''} ${openOnClick && onRowOpen ? 'grid-row-open' : ''} ${rowClass?.(row) ?? ''}`}
                  style={{ height: effectiveRowHeight }}
                  onClick={(e) => {
                    setFocused(item.index);
                    if (selectable && (e.ctrlKey || e.metaKey || e.shiftKey)) toggleRow(item.index, e.shiftKey);
                    else if (openOnClick && !editing && !isInteractiveTarget(e.target, e.currentTarget)) onRowOpen?.(row);
                  }}
                  onDoubleClick={() => {
                    if (!editing && !openOnClick) onRowOpen?.(row);
                  }}
                  data-testid={`row-${key}`}
                >
                  {selectable && (
                    <td className="grid-cell-check pin-left">
                      <input type="checkbox" aria-label={`Select ${key}`} checked={isSelected} onChange={(e) => toggleRow(item.index, (e.nativeEvent as MouseEvent).shiftKey)} />
                    </td>
                  )}
                  {visible.map((c) => {
                    const pinnedL = prefs.pinLeft.includes(c.key);
                    const pinnedR = prefs.pinRight.includes(c.key);
                    const isEditing = editing?.row === key && editing.col === c.key;
                    return (
                      <td
                        key={c.key}
                        className={`${pinnedL ? 'pin-left' : pinnedR ? 'pin-right' : ''} ${isEditing ? 'editing' : ''}`}
                        style={{ left: pinnedL && selectable ? SELECT_WIDTH : undefined, textAlign: c.align }}
                        data-col={c.key}
                        onDoubleClick={(e) => {
                          if (c.editor) {
                            e.stopPropagation();
                            setEditing({ row: key, col: c.key });
                          }
                        }}
                        onPaste={(e) => {
                          if (c.onPaste && !isEditing) {
                            e.preventDefault();
                            c.onPaste(row, e.clipboardData.getData('text'));
                          }
                        }}
                        tabIndex={c.onPaste || c.editor ? 0 : undefined}
                        onKeyDown={(e) => {
                          if (c.editor && !isEditing && (e.key === 'F2' || e.key === 'Enter')) {
                            e.preventDefault();
                            e.stopPropagation();
                            setEditing({ row: key, col: c.key });
                          } else if (isEditing && e.key === 'Tab') {
                            // Tab commits (the editors commit on blur) and opens the next editable cell
                            e.preventDefault();
                            e.stopPropagation();
                            const next = nextEditable(item.index, c.key, e.shiftKey);
                            (e.target as HTMLElement).blur?.();
                            setEditing(next);
                          }
                        }}
                      >
                        {isEditing && c.editor
                          ? c.editor(row, {
                              commit: () => setEditing(null),
                              cancel: () => setEditing(null),
                            })
                          : c.render(row)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr aria-hidden="true">
                <td style={{ height: paddingBottom, padding: 0, border: 'none' }} colSpan={visible.length + (selectable ? 1 : 0)} />
              </tr>
            )}
          </tbody>
        </table>
        {sortedRows.length === 0 && !loading && <div className="grid-empty">{typeof emptyState === 'string' || emptyState === undefined ? <EmptyState title={(emptyState as string) ?? 'Nothing here'} /> : emptyState}</div>}
        {loading && pageRows.length === 0 && (
          <div className="skeleton-rows" role="status">
            <span className="visually-hidden">Loading…</span>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton-row" aria-hidden="true">
                <span className="skeleton" style={{ maxWidth: 80 }} />
                <span className="skeleton" />
                <span className="skeleton" style={{ maxWidth: 120 }} />
                <span className="skeleton" />
              </div>
            ))}
          </div>
        )}
        {loading && pageRows.length > 0 && (
          <div className="grid-empty loading-line" role="status">
            <span className="spinner" aria-hidden="true" /> Loading…
          </div>
        )}
      </div>
      {showPager ? (
        <Pager ref={pagerRef} start={start} shown={pageRows.length} count={count} totalKnown={totalKnown} page={page} pages={pages} size={size} onPage={goTo} onSize={setPageSize} />
      ) : (
        !paged &&
        (hasMore || onLoadMore) && (
          <div className="grid-footer">
            <span>{sortedRows.length} loaded{hasMore ? ', more available' : ''}</span>
            {hasMore && onLoadMore && (
              <Button size="s" onClick={onLoadMore} busy={loading}>
                Load more
              </Button>
            )}
          </div>
        )
      )}
      <Menu
        anchor={menu?.anchor ?? null}
        open={menu !== null}
        onClose={() => setMenu(null)}
        label="Column menu"
        items={[
          { key: 'asc', label: 'Sort ascending', disabled: !menuColumn || (!menuColumn.sortValue && !onSortChange && !menuColumn.sortable), onSelect: () => menu && (onSortChange ? onSortChange({ key: menu.col, dir: 'asc' }) : setClientSort({ key: menu.col, dir: 'asc' })) },
          { key: 'desc', label: 'Sort descending', disabled: !menuColumn || (!menuColumn.sortValue && !onSortChange && !menuColumn.sortable), onSelect: () => menu && (onSortChange ? onSortChange({ key: menu.col, dir: 'desc' }) : setClientSort({ key: menu.col, dir: 'desc' })) },
          { key: 'nosort', label: 'Clear sort', disabled: !effectiveSort, onSelect: () => (onSortChange ? onSortChange(null) : setClientSort(null)) },
          { key: 's1', label: '', separator: true },
          { key: 'pl', label: menu && prefs.pinLeft.includes(menu.col) ? 'Unpin' : 'Pin left', onSelect: () => menu && setPin(menu.col, prefs.pinLeft.includes(menu.col) ? null : 'left') },
          { key: 'pr', label: menu && prefs.pinRight.includes(menu.col) ? 'Unpin' : 'Pin right', onSelect: () => menu && setPin(menu.col, prefs.pinRight.includes(menu.col) ? null : 'right') },
          { key: 'hide', label: 'Hide', disabled: menuColumn?.fixed, onSelect: () => menu && setHidden(menu.col, true) },
        ]}
      />
      <span className="visually-hidden" aria-live="polite">
        {toggleSort === undefined ? '' : ''}
      </span>
    </div>
  );
}

interface PagerProps {
  ref?: Ref<HTMLElement>;
  start: number;
  shown: number;
  count: number;
  /** false while a server-paged list has more than it has loaded and no total: the last page is not known yet */
  totalKnown: boolean;
  page: number;
  pages: number;
  size: number;
  onPage: (page: number) => void;
  onSize: (size: number) => void;
}

/** The pager under a list longer than one page: the rows on screen of the count, rows per page, first / previous / page box / next / last. */
function Pager({ ref, start, shown, count, totalKnown, page, pages, size, onPage, onSize }: PagerProps) {
  const last = totalKnown ? pages - 1 : pages;
  const jump = (text: string) => {
    const n = Number.parseInt(text, 10);
    if (Number.isFinite(n) && n !== page + 1) onPage(Math.min(Math.max(n, 1) - 1, last));
  };
  return (
    <nav ref={ref} className="grid-footer grid-pager" aria-label="Pages">
      <span className="grid-pager-range" aria-live="polite">
        {shown > 0 ? `${formatCount(start + 1)}–${formatCount(start + shown)}` : formatCount(start)} of {formatCount(count)}
        {totalKnown ? '' : '+'}
      </span>
      <span className="spacer" />
      <label className="grid-pager-size">
        <span>Rows per page</span>
        <Select size="s" className="input-inline" value={size} onChange={(e) => onSize(Number(e.target.value))}>
          {(PAGE_SIZES.includes(size) ? PAGE_SIZES : [...PAGE_SIZES, size].sort((a, b) => a - b)).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Select>
      </label>
      <span className="grid-pager-nav">
        <IconButton label="First page" disabled={page === 0} onClick={() => onPage(0)}>
          <ChevronsLeft aria-hidden="true" />
        </IconButton>
        <IconButton label="Previous page" disabled={page === 0} onClick={() => onPage(page - 1)}>
          <ChevronLeft aria-hidden="true" />
        </IconButton>
        <span className="grid-pager-page">
          Page
          <TextInput
            key={page}
            size="s"
            inputMode="numeric"
            aria-label="Page"
            defaultValue={page + 1}
            onKeyDown={(e) => {
              if (e.key === 'Enter') jump(e.currentTarget.value);
            }}
            onBlur={(e) => jump(e.currentTarget.value)}
          />
          of {formatCount(pages)}
          {totalKnown ? '' : '+'}
        </span>
        <IconButton label="Next page" disabled={page >= last} onClick={() => onPage(page + 1)}>
          <ChevronRight aria-hidden="true" />
        </IconButton>
        <IconButton label="Last page" disabled={!totalKnown || page >= last} onClick={() => onPage(last)}>
          <ChevronsRight aria-hidden="true" />
        </IconButton>
      </span>
    </nav>
  );
}
